import type {Announcements} from '@dnd-kit/core';

import {getItemAccessibleName} from '../../shared/accessibleName';
import type {DragProgress} from '../../shared/dragProgress';
import type {DropSlot, WordBankItem} from '../store';
import {WORD_BANK_ID} from '../store';

/** "slot 2 of 4", or "a slot" for an id the question doesn't have. */
const slotName = (dropSlots: DropSlot[], slotId: string): string => {
  const index = dropSlots.findIndex((slot) => slot.id === slotId);
  return index === -1 ? 'a slot' : `slot ${index + 1} of ${dropSlots.length}`;
};

/** Where an item is: its slot, or the item bank. */
const location = (dropSlots: DropSlot[], item: WordBankItem): string =>
  item.placedInSlot ? slotName(dropSlots, item.placedInSlot) : 'the item bank';

/**
 * The spoken name of a drop slot: its position, its prompt, then the item it holds. `null` is
 * an empty slot; an item whose name strips to nothing is still an item.
 */
export const slotAccessibleName = (
  index: number,
  total: number,
  promptName: string,
  itemName: string | null
): string => {
  const contents = itemName === null ? 'empty' : itemName || 'Item';
  return [`Slot ${index + 1} of ${total}`, promptName, contents].filter(Boolean).join(', ');
};

export const selectedMessage = (dropSlots: DropSlot[], item: WordBankItem): string => {
  const name = getItemAccessibleName(item.value);
  return item.placedInSlot
    ? `${name} selected from ${slotName(dropSlots, item.placedInSlot)}. Choose a slot or the item bank.`
    : `${name} selected. Choose a slot.`;
};

export const deselectedMessage = (item: WordBankItem): string =>
  `${getItemAccessibleName(item.value)} deselected.`;

/**
 * What `placeItemInSlot(itemId, targetId)` does, described from the state before it ran: the
 * item lands in the slot or back in the bank, and whatever held the slot returns to the bank.
 */
export const placementMessage = (
  wordBank: WordBankItem[],
  dropSlots: DropSlot[],
  itemId: string,
  targetId: string
): string => {
  const item = wordBank.find((candidate) => candidate.id === itemId);
  if (!item) return '';
  const name = getItemAccessibleName(item.value);

  if (targetId === WORD_BANK_ID) {
    if (!item.placedInSlot) return `${name} stays in the item bank.`;
    return `${name} removed from ${slotName(dropSlots, item.placedInSlot)} and returned to the item bank.`;
  }
  if (item.placedInSlot === targetId) return `${name} stays in ${slotName(dropSlots, targetId)}.`;

  const displaced = wordBank.find(
    (candidate) => candidate.id !== itemId && candidate.placedInSlot === targetId
  );
  const placed = `${name} placed in ${slotName(dropSlots, targetId)}.`;
  return displaced
    ? `${placed} ${getItemAccessibleName(displaced.value)} returned to the item bank.`
    : placed;
};

/**
 * dnd-kit announcements for the item bank, spoken through DndContext's own live region. They
 * read the bank and slots as rendered: DndContext runs our drop handler before it announces,
 * so on drop the store has already placed the item, but the pre-drop state describes the same
 * move.
 */
export const itemBankAnnouncements = (
  wordBank: WordBankItem[],
  dropSlots: DropSlot[],
  progress: DragProgress = {hasLeftOrigin: false}
): Announcements => {
  const find = (id: string | number): WordBankItem | undefined =>
    wordBank.find((item) => item.id === id);
  const stays = (item: WordBankItem): string =>
    `${getItemAccessibleName(item.value)} stays in ${location(dropSlots, item)}.`;

  return {
    onDragStart: ({active}) => {
      const item = find(active.id);
      if (!item) return undefined;
      progress.hasLeftOrigin = false;
      return `Picked up ${getItemAccessibleName(item.value)} from ${location(dropSlots, item)}.`;
    },
    onDragOver: ({active, over}) => {
      const item = find(active.id);
      if (!item) return undefined;
      const origin = item.placedInSlot ?? WORD_BANK_ID;
      if (over?.id === origin && !progress.hasLeftOrigin) return undefined;
      progress.hasLeftOrigin = true;
      const name = getItemAccessibleName(item.value);
      if (!over) return `${name} is not over a slot.`;
      if (over.id === WORD_BANK_ID) return `${name} is over the item bank.`;
      const slotId = over.id.toString();
      const occupant = wordBank.find(
        (candidate) => candidate.id !== item.id && candidate.placedInSlot === slotId
      );
      const over_ = `${name} is over ${slotName(dropSlots, slotId)}`;
      return occupant
        ? `${over_}, which holds ${getItemAccessibleName(occupant.value)}.`
        : `${over_}.`;
    },
    onDragEnd: ({active, over}) => {
      const item = find(active.id);
      if (!item) return undefined;
      if (!over || over.id === active.id) return stays(item);
      return placementMessage(wordBank, dropSlots, item.id, over.id.toString());
    },
    onDragCancel: ({active}) => {
      const item = find(active.id);
      if (!item) return undefined;
      return `Move cancelled. ${stays(item)}`;
    }
  };
};
